import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, Trash2, Star, StarOff, ChevronDown, ChevronUp, Settings, Plus, Zap, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../utils/api';

const ManagementNotes = () => {
  const [notes, setNotes] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showRules, setShowRules] = useState(false);
  
  // New note form
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteAiRelevant, setNewNoteAiRelevant] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Action note form (extended fields)
  const [isActionNote, setIsActionNote] = useState(false);
  const [actionType, setActionType] = useState('');
  const [expectedEffect, setExpectedEffect] = useState('');
  const [expectedDays, setExpectedDays] = useState('');
  const [affectedMetrics, setAffectedMetrics] = useState([]);
  
  // New rule form
  const [showNewRuleForm, setShowNewRuleForm] = useState(false);
  const [newRuleTitle, setNewRuleTitle] = useState('');
  const [newRuleDescription, setNewRuleDescription] = useState('');

  const actionTypes = [
    { value: 'budget_change', label: 'Budget ændring' },
    { value: 'campaign', label: 'Kampagne' },
    { value: 'staffing', label: 'Bemanding' },
    { value: 'pricing', label: 'Pris ændring' },
    { value: 'process', label: 'Proces ændring' },
    { value: 'other', label: 'Andet' },
  ];

  const metricOptions = [
    { value: 'nye_bookinger', label: 'Nye bookinger' },
    { value: 'belaegning', label: 'Belægning' },
    { value: 'kpt', label: 'KPT' },
    { value: 'aflysninger', label: 'Aflysninger' },
    { value: 'omsaetning', label: 'Omsætning' },
    { value: 'konvertering', label: 'Konvertering' },
  ];

  // Fetch notes and rules
  const fetchData = async () => {
    setLoading(true);
    try {
      const [notesRes, rulesRes] = await Promise.all([
        api.get('/notes?days=30&include_ai_relevant=true'),
        api.get('/rules?active_only=false')
      ]);
      setNotes(notesRes.data.notes || []);
      setRules(rulesRes.data.rules || []);
    } catch (err) {
      console.error('Error fetching notes/rules:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Create note
  const handleCreateNote = async () => {
    if (!newNoteText.trim() || submitting) return;
    
    setSubmitting(true);
    try {
      const noteData = {
        text: newNoteText,
        ai_relevant: newNoteAiRelevant || isActionNote,
        is_action: isActionNote,
        action_type: isActionNote ? actionType : null,
        expected_effect: isActionNote ? expectedEffect : null,
        expected_days: isActionNote && expectedDays ? parseInt(expectedDays) : null,
        affected_metrics: isActionNote && affectedMetrics.length > 0 ? affectedMetrics : null,
        tag: isActionNote ? 'handling' : null,
      };
      
      const response = await api.post('/notes', noteData);
      console.log('Create note response:', response);
      
      // Reset form
      setNewNoteText('');
      setNewNoteAiRelevant(false);
      setIsActionNote(false);
      setActionType('');
      setExpectedEffect('');
      setExpectedDays('');
      setAffectedMetrics([]);
      
      await fetchData();
    } catch (err) {
      console.error('Error creating note:', err);
      alert(`Kunne ikke oprette noten: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMetric = (metric) => {
    setAffectedMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    console.log('Attempting to delete note:', noteId);
    if (!window.confirm('Er du sikker på, at du vil slette denne note?')) {
      console.log('Delete cancelled by user');
      return;
    }
    
    try {
      console.log('Sending DELETE request to /notes/' + noteId);
      const response = await api.delete(`/notes/${noteId}`);
      console.log('Delete response:', response);
      await fetchData();
    } catch (err) {
      console.error('Error deleting note:', err);
      console.error('Error response:', err.response);
      alert(`Kunne ikke slette noten: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Toggle AI relevant
  const handleToggleAiRelevant = async (noteId, currentValue) => {
    try {
      await api.patch(`/notes/${noteId}`, { ai_relevant: !currentValue });
      await fetchData();
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  // Create rule
  const handleCreateRule = async () => {
    if (!newRuleTitle.trim() || !newRuleDescription.trim()) return;
    
    try {
      await api.post('/rules', {
        title: newRuleTitle,
        description: newRuleDescription,
        active: true
      });
      setNewRuleTitle('');
      setNewRuleDescription('');
      setShowNewRuleForm(false);
      await fetchData();
    } catch (err) {
      console.error('Error creating rule:', err);
    }
  };

  // Toggle rule active
  const handleToggleRule = async (ruleId, currentValue) => {
    try {
      await api.patch(`/rules/${ruleId}`, { active: !currentValue });
      await fetchData();
    } catch (err) {
      console.error('Error updating rule:', err);
    }
  };

  // Delete rule
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Er du sikker på, at du vil slette denne regel?')) return;
    
    try {
      await api.delete(`/rules/${ruleId}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/10 overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <MessageSquarePlus className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Ledelsesnoter</h3>
            <p className="text-white/50 text-xs">Noter og kontekst til AI-analysen</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-white/40">{notes.length} noter</span>
          {expanded ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10">
          {/* New Note Form */}
          <div className="p-4 border-b border-white/10 bg-white/5">
            <div className="space-y-3">
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder={isActionNote 
                  ? "Beskriv den handling du har foretaget (fx: 'Vi har skruet marketingbudgettet op med 10% pga. faldende KPT i uge 2-3')"
                  : "Skriv en note til ledelsen og AI-analysen..."
                }
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-amber-500/50 placeholder:text-white/30 resize-none"
                rows={2}
              />
              
              {/* Toggle between regular note and action note */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isActionNote}
                    onChange={(e) => setIsActionNote(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-white/60 flex items-center gap-1">
                    <Zap className="w-4 h-4 text-blue-400" />
                    Dette er en handling (AI skal evaluere effekt)
                  </span>
                </label>
              </div>

              {/* Action-specific fields */}
              {isActionNote && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-3">
                  <p className="text-xs text-blue-300">
                    Udfyld felterne herunder så AI kan evaluere effekten af din handling ved næste analyse.
                  </p>
                  
                  {/* Action type */}
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Type handling</label>
                    <select
                      value={actionType}
                      onChange={(e) => setActionType(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none"
                    >
                      <option value="">Vælg type...</option>
                      {actionTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Expected effect */}
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Forventet effekt</label>
                    <input
                      type="text"
                      value={expectedEffect}
                      onChange={(e) => setExpectedEffect(e.target.value)}
                      placeholder="fx: 'KPT stiger til normalt niveau (0.40+)'"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none"
                    />
                  </div>

                  {/* Expected days */}
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Forventet effekt inden (dage)</label>
                    <input
                      type="number"
                      value={expectedDays}
                      onChange={(e) => setExpectedDays(e.target.value)}
                      placeholder="fx: 6"
                      min="1"
                      max="90"
                      className="w-32 bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none"
                    />
                  </div>

                  {/* Affected metrics */}
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Påvirkede metrics</label>
                    <div className="flex flex-wrap gap-2">
                      {metricOptions.map(m => (
                        <button
                          key={m.value}
                          onClick={() => toggleMetric(m.value)}
                          className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                            affectedMetrics.includes(m.value)
                              ? 'bg-blue-500/30 border-blue-500 text-blue-300'
                              : 'bg-white/5 border-white/20 text-white/50 hover:border-white/40'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap items-center gap-3">
                {/* AI relevant checkbox - only show if not action note */}
                {!isActionNote && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newNoteAiRelevant}
                      onChange={(e) => setNewNoteAiRelevant(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm text-white/60">Altid relevant for AI</span>
                    <Star className={`w-4 h-4 ${newNoteAiRelevant ? 'text-amber-400' : 'text-white/20'}`} />
                  </label>
                )}
                
                <div className="flex-1" />
                
                {/* Submit button */}
                <button
                  onClick={handleCreateNote}
                  disabled={!newNoteText.trim() || submitting || (isActionNote && (!actionType || !expectedEffect || !expectedDays))}
                  className={`px-4 py-1.5 ${isActionNote ? 'bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/30' : 'bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/30'} disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors flex items-center gap-2`}
                >
                  <Plus className="w-4 h-4" />
                  {isActionNote ? 'Registrer handling' : 'Tilføj note'}
                </button>
              </div>
            </div>
          </div>

          {/* Notes List */}
          <div className="p-4">
            {loading ? (
              <p className="text-white/40 text-sm text-center py-4">Indlæser noter...</p>
            ) : notes.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-4">Ingen noter endnu. Tilføj den første note ovenfor.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.note_id}
                    className={`p-3 rounded-lg border ${
                      note.is_action 
                        ? 'bg-blue-500/10 border-blue-500/30' 
                        : note.ai_relevant 
                          ? 'bg-amber-500/10 border-amber-500/30' 
                          : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {note.is_action ? (
                            <>
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                Handling
                              </span>
                              {note.action_type && (
                                <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/60">
                                  {actionTypes.find(t => t.value === note.action_type)?.label || note.action_type}
                                </span>
                              )}
                              {note.effect_expected_by && (
                                <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/50 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Effekt inden {note.effect_expected_by}
                                </span>
                              )}
                              {note.effect_evaluated && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Evalueret
                                </span>
                              )}
                            </>
                          ) : note.ai_relevant && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              AI-relevant
                            </span>
                          )}
                        </div>
                        <p className="text-white/90 text-sm">{note.text}</p>
                        
                        {/* Show action details */}
                        {note.is_action && (
                          <div className="mt-2 p-2 bg-white/5 rounded text-xs space-y-1">
                            {note.expected_effect && (
                              <p className="text-white/60">
                                <span className="text-white/40">Forventet effekt:</span> {note.expected_effect}
                              </p>
                            )}
                            {note.affected_metrics && note.affected_metrics.length > 0 && (
                              <p className="text-white/60">
                                <span className="text-white/40">Påvirkede metrics:</span> {note.affected_metrics.map(m => 
                                  metricOptions.find(o => o.value === m)?.label || m
                                ).join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        
                        <p className="text-white/40 text-xs mt-2">
                          {note.user_name} · {formatDate(note.created_at)}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {!note.is_action && (
                          <button
                            onClick={() => handleToggleAiRelevant(note.note_id, note.ai_relevant)}
                            className="p-1.5 hover:bg-white/10 rounded transition-colors"
                            title={note.ai_relevant ? 'Fjern AI-markering' : 'Markér som AI-relevant'}
                          >
                            {note.ai_relevant ? (
                              <Star className="w-4 h-4 text-amber-400" />
                            ) : (
                              <StarOff className="w-4 h-4 text-white/30" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNote(note.note_id)}
                          className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                          title="Slet note"
                        >
                          <Trash2 className="w-4 h-4 text-white/30 hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Permanent Rules Section */}
          <div className="border-t border-white/10">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setShowRules(!showRules)}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-white/40" />
                <span className="text-white/80 text-sm">Permanente driftsregler</span>
                <span className="text-xs text-white/40">({rules.filter(r => r.active).length} aktive)</span>
              </div>
              {showRules ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
            </div>
            
            {showRules && (
              <div className="p-4 pt-0">
                <p className="text-white/40 text-xs mb-3">
                  Driftsregler som AI altid tager højde for i sin analyse.
                </p>
                
                {/* Rules List */}
                <div className="space-y-2 mb-4">
                  {rules.map((rule) => (
                    <div
                      key={rule.rule_id}
                      className={`p-3 rounded-lg border ${rule.active ? 'bg-white/5 border-white/10' : 'bg-white/2 border-white/5 opacity-50'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="text-white/90 text-sm font-medium">{rule.title}</h4>
                          <p className="text-white/60 text-xs mt-1">{rule.description}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleToggleRule(rule.rule_id, rule.active)}
                            className={`px-2 py-1 text-xs rounded ${rule.active ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white/40'}`}
                          >
                            {rule.active ? 'Aktiv' : 'Inaktiv'}
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.rule_id)}
                            className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-white/30 hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {rules.length === 0 && !showNewRuleForm && (
                    <p className="text-white/40 text-xs text-center py-2">Ingen driftsregler oprettet endnu.</p>
                  )}
                </div>
                
                {/* New Rule Form */}
                {showNewRuleForm ? (
                  <div className="p-3 bg-white/5 rounded-lg border border-white/10 space-y-3">
                    <input
                      type="text"
                      value={newRuleTitle}
                      onChange={(e) => setNewRuleTitle(e.target.value)}
                      placeholder="Titel (fx 'Weekend lukketid')"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none"
                    />
                    <textarea
                      value={newRuleDescription}
                      onChange={(e) => setNewRuleDescription(e.target.value)}
                      placeholder="Beskrivelse (fx 'Kundeservice er lukket lørdag og søndag, derfor kommer der færre bookinger i weekenden.')"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none resize-none"
                      rows={2}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setShowNewRuleForm(false);
                          setNewRuleTitle('');
                          setNewRuleDescription('');
                        }}
                        className="px-3 py-1.5 text-white/60 text-sm hover:bg-white/10 rounded-lg transition-colors"
                      >
                        Annuller
                      </button>
                      <button
                        onClick={handleCreateRule}
                        disabled={!newRuleTitle.trim() || !newRuleDescription.trim()}
                        className="px-3 py-1.5 bg-green-500 hover:bg-green-600 disabled:bg-green-500/30 text-white text-sm rounded-lg transition-colors"
                      >
                        Gem regel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewRuleForm(true)}
                    className="w-full p-2 border border-dashed border-white/20 rounded-lg text-white/40 text-sm hover:border-white/40 hover:text-white/60 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Tilføj ny driftsregel
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementNotes;
